import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/RolePermission';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const ROLE_PERMISSION_API_REDUCER_KEY = 'role-permissions';
export const ROLE_PERMISSION_PATH = 'role-permissions';

export const rolePermissionApi = createApi({
    reducerPath: ROLE_PERMISSION_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['RolePermission', 'RolePermissions'],
    endpoints: (builder) => ({
        getRolePermissionList: builder.query<PaginationResult<Entity.RolePermission>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ ROLE_PERMISSION_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['RolePermissions'],
        }),
        getRolePermission: builder.query<Entity.RolePermission, Entity.PrimaryRolePermission>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = rolePermissionNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ ROLE_PERMISSION_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['RolePermission'],
        }),
        getRolePermissionMultiple: builder.query<Entity.RolePermission, Entity.PrimaryRolePermission[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROLE_PERMISSION_PATH }/ids${Entity.PrimaryRolePermission.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['RolePermissions'],
        }),
        getRolePermissionPresentation: builder.query<PaginationResult<Entity.RolePermission>, Entity.PresentationRolePermissionRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ ROLE_PERMISSION_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['RolePermissions'],
        }),
        getRolePermissionAdvancedPresentation: builder.query<PaginationResult<Entity.RolePermission>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ ROLE_PERMISSION_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['RolePermissions'],
        }),
        
        insertRolePermission: builder.mutation<Entity.RolePermission, Entity.CreateRolePermission>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROLE_PERMISSION_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    rolePermissionApi.util.upsertQueryData('getRolePermission', Entity.PrimaryRolePermission.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['RolePermissions'],
        }),
        insertMultipleRolePermission: builder.mutation<Entity.RolePermission[], Entity.CreateRolePermission[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROLE_PERMISSION_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            rolePermissionApi.util.upsertQueryData('getRolePermission', Entity.PrimaryRolePermission.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['RolePermissions'],
        }),
        
        deleteRolePermission: builder.mutation<Entity.RolePermission, Entity.RolePermission>({
            query: (toDelete) => {
                return ({
                    url: `/${ ROLE_PERMISSION_PATH }/${Entity.PrimaryRolePermission.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['RolePermissions'],
        }),
        deleteMultipleRolePermission: builder.mutation<Entity.RolePermission[], Entity.PrimaryRolePermission[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROLE_PERMISSION_PATH }/batch${Entity.PrimaryRolePermission.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['RolePermissions'],
        }),
    }),
})

export const [rolePermissionNormalizer, rolePermissionNormalizerMiddleware] = createNormalizationMiddleware(ROLE_PERMISSION_API_REDUCER_KEY, rolePermissionApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetRolePermissionListQuery,
    useGetRolePermissionMultipleQuery,
    useGetRolePermissionPresentationQuery,
    useGetRolePermissionQuery,
    useInsertRolePermissionMutation,
    useInsertMultipleRolePermissionMutation,
    
    useDeleteRolePermissionMutation,
    useDeleteMultipleRolePermissionMutation,
    useGetRolePermissionAdvancedPresentationQuery,
    
} = rolePermissionApi
